import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { BarChart3, Download, Users, Eye, MousePointer, ThumbsUp, ThumbsDown, RefreshCw, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function useFetch(url, token) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [url, token]);
  useEffect(reload, [reload]);
  return { data, loading, reload };
}

function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="bg-card border-border/50">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10"><Icon className="w-5 h-5 text-primary" /></div>
        <div>
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-xl font-bold text-foreground">{value}</p>
          {sub && <p className="text-2xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsDashboard() {
  const { token, user } = useAuth();
  const { tenantId: urlTenant } = useParams();
  const [days, setDays] = useState(7);
  const [tenantFilter, setTenantFilter] = useState(urlTenant || '');
  const [accessibleTenants, setAccessibleTenants] = useState([]);
  const [eventFilter, setEventFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');

  // Fetch accessible tenants on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/analytics/tenants`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setAccessibleTenants(d.tenants || []);
        // Default to current URL tenant if it's in the list
        if (urlTenant && (d.tenants || []).includes(urlTenant)) {
          setTenantFilter(urlTenant);
        }
      })
      .catch(() => {});
  }, [token, urlTenant]);

  // Build query params with tenant filter
  const tenantParam = tenantFilter ? `&tenant_id=${tenantFilter}` : '';

  const summaryUrl = `${API_BASE}/api/analytics/summary?days=${days}${tenantParam}`;
  const { data: summary, loading: summaryLoading, reload: reloadSummary } = useFetch(summaryUrl, token);

  const eventsParams = new URLSearchParams({ days: String(days), limit: '100' });
  if (eventFilter) eventsParams.set('event', eventFilter);
  if (routeFilter) eventsParams.set('route', routeFilter);
  if (tenantFilter) eventsParams.set('tenant_id', tenantFilter);
  const eventsUrl = `${API_BASE}/api/analytics/events?${eventsParams}`;
  const { data: eventsData, loading: eventsLoading, reload: reloadEvents } = useFetch(eventsUrl, token);

  const handleExportCsv = async () => {
    try {
      const exportParams = new URLSearchParams({ days: String(days) });
      if (tenantFilter) exportParams.set('tenant_id', tenantFilter);
      const res = await fetch(`${API_BASE}/api/analytics/export?${exportParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ybv_analytics_${tenantFilter || 'all'}_${days}d.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center" data-testid="analytics-access-denied">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <BarChart3 className="w-6 h-6 text-destructive" />
            </div>
            <p className="text-sm font-medium text-foreground">Access Restricted</p>
            <p className="text-xs text-muted-foreground">This page is available to administrators only.</p>
          </div>
        </main>
      </div>
    );
  }

  const ec = summary?.event_counts || {};
  const fb = summary?.feedback || {};

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-primary/10 text-primary border-primary/30 border text-xs">Analytics</Badge>
                <Badge variant="outline" className="text-2xs">Admin Only</Badge>
                {tenantFilter && <Badge variant="outline" className="text-2xs border-primary/40 text-primary">{tenantFilter}</Badge>}
              </div>
              <h1 className="text-xl font-bold text-foreground" data-testid="analytics-title">Product Analytics</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Tenant filter */}
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                <select
                  value={tenantFilter}
                  onChange={(e) => setTenantFilter(e.target.value)}
                  className="h-8 px-2 text-xs bg-background border border-border/50 rounded text-foreground"
                  data-testid="analytics-tenant-filter"
                >
                  <option value="">All workspaces</option>
                  {accessibleTenants.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="h-8 px-2 text-xs bg-background border border-border/50 rounded text-foreground"
                data-testid="analytics-days-filter"
              >
                <option value={1}>Last 24h</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <Button variant="outline" size="sm" onClick={() => { reloadSummary(); reloadEvents(); }} data-testid="analytics-refresh">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="analytics-export-csv">
                <Download className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
            </div>
          </div>

          {/* KPI Cards */}
          {!summaryLoading && summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={MousePointer} label="Total Events" value={summary.total_events} sub={`${days}-day window`} />
              <KpiCard icon={Users} label="Unique Users" value={summary.unique_users} />
              <KpiCard icon={Eye} label="Page Views" value={ec.page_view || 0} />
              <KpiCard icon={ThumbsUp} label="Feedback" value={`${fb.yes || 0} / ${fb.no || 0}`} sub="Yes / No" />
            </div>
          )}

          {/* Event Breakdown + Top Routes */}
          {!summaryLoading && summary && (
            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Event Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(ec).sort((a,b) => b[1] - a[1]).map(([name, count]) => (
                      <div key={name} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground font-mono">{name}</span>
                        <Badge variant="outline" className="text-2xs">{count}</Badge>
                      </div>
                    ))}
                    {Object.keys(ec).length === 0 && <p className="text-xs text-muted-foreground">No events yet</p>}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Top Routes</h3>
                  <div className="space-y-2">
                    {(summary.top_routes || []).map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground font-mono truncate max-w-[200px]">{r.route}</span>
                        <Badge variant="outline" className="text-2xs">{r.count} views</Badge>
                      </div>
                    ))}
                    {(summary.top_routes || []).length === 0 && <p className="text-xs text-muted-foreground">No page views yet</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Event Log */}
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">Event Log</h3>
                <div className="flex items-center gap-2">
                  <select
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    className="h-7 px-2 text-2xs bg-background border border-border/50 rounded text-foreground"
                    data-testid="event-type-filter"
                  >
                    <option value="">All events</option>
                    <option value="page_view">page_view</option>
                    <option value="login_success">login_success</option>
                    <option value="scenario_change">scenario_change</option>
                    <option value="export_pdf_click">export_pdf_click</option>
                    <option value="settings_change_dataset_mode">settings_change</option>
                    <option value="feedback">feedback</option>
                  </select>
                  <Input
                    placeholder="Filter route..."
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                    className="h-7 w-32 text-2xs"
                    data-testid="route-filter"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="event-log-table">
                  <thead>
                    <tr className="border-b border-border/30">
                      <th className="text-left py-2 px-2 text-muted-foreground font-medium">Time</th>
                      <th className="text-left py-2 px-2 text-muted-foreground font-medium">Event</th>
                      <th className="text-left py-2 px-2 text-muted-foreground font-medium">Route</th>
                      <th className="text-left py-2 px-2 text-muted-foreground font-medium">Tenant</th>
                      <th className="text-left py-2 px-2 text-muted-foreground font-medium">User</th>
                      <th className="text-left py-2 px-2 text-muted-foreground font-medium">Meta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(eventsData?.events || []).map((e, i) => (
                      <tr key={i} className="border-b border-border/10 hover:bg-muted/20">
                        <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">
                          {new Date(e.timestamp).toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                        </td>
                        <td className="py-1.5 px-2"><code className="text-primary">{e.event}</code></td>
                        <td className="py-1.5 px-2 text-muted-foreground font-mono truncate max-w-[150px]">{e.route}</td>
                        <td className="py-1.5 px-2">
                          <Badge variant="outline" className="text-2xs">{e.tenant_id}</Badge>
                        </td>
                        <td className="py-1.5 px-2 text-muted-foreground">{e.user_hash} <span className="text-2xs">({e.user_role})</span></td>
                        <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[120px]">{JSON.stringify(e.metadata)}</td>
                      </tr>
                    ))}
                    {(!eventsData?.events || eventsData.events.length === 0) && (
                      <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No events found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
